import { Flex, Grid } from '@sequencehq/core-components'
import { SidebarField } from 'Cube/view/layouts/quote/QuoteEditorSidebar/widgets/SidebarField'
import {
  ContractDurationSelectControl,
  CounterSignerEditorControl,
  CurrencyEditorControl,
  CustomerEditorControl,
  DealTypeEditorControl,
  EffectiveFromSelectControl,
  ExpirySelectControl,
  PaymentTermsSelectControl,
  StartDateSelectControl
} from 'modules/Cube/view/layouts/quote/QuoteEditorControls/QuoteEditorControls'
import { RecipientsEditorControl } from 'modules/Cube/view/layouts/quote/QuoteEditorControls/RecipientsSelect'
import { UseQuoteEditorSidebarInterface } from 'Cube/view/layouts/quote/QuoteEditorSidebar/useQuoteEditorSidebar'
import { CreateRecipientModal } from '../../QuoteEditorControls/CreateRecipientModal/CreateRecipientModal'
import { SelectScheduleForAmendment } from '../../QuoteEditorControls/SelectScheduleForAmendment'
import { ConfirmAmendmentChange } from '../../QuoteEditorControls/ConfirmAmendmentChange'
import { AmendedScheduleField } from 'Cube/view/layouts/quote/QuoteEditorSidebar/widgets/AmendedScheduleField'

type Props = {
  hook: UseQuoteEditorSidebarInterface
}

export const SidebarFields = ({ hook }: Props) => {
  const customerId = hook.fields.customer.value

  return (
    <Grid
      width="100%"
      gridTemplateColumns="1fr 154px"
      alignItems="center"
      marginBottom="16px"
      data-testid="quote.configSidebar"
    >
      <Flex flexDirection="column" width="288px">
        {hook.fields.customer.visible && (
          <SidebarField
            label="Customer"
            control={
              <CustomerEditorControl
                type="sidebar"
                data-testid="quote.customerControl"
                value={hook.fields.customer.value}
                onChange={hook.fields.customer.onChange}
                disabled={hook.fields.customer.isDisabled}
                validationErrors={hook.fields.customer.validationErrors}
              />
            }
          />
        )}

        {hook.fields.recipients.visible && (
          <SidebarField
            label="Recipients"
            control={
              <RecipientsEditorControl
                type="sidebar"
                data-testid="quote.recipientsControl"
                options={hook.fields.recipients.options}
                value={hook.fields.recipients.value}
                onChange={hook.fields.recipients.onChange}
                onCreate={hook.features.createRecipient.onClick}
                validationErrors={hook.fields.recipients.validationErrors}
                disabled={hook.fields.recipients.isDisabled}
              />
            }
          />
        )}

        {hook.fields.counterSigners.visible && (
          <SidebarField
            label="Int. signers"
            control={
              <CounterSignerEditorControl
                type="sidebar"
                data-testid="quote.counterSignersControl"
                options={hook.fields.counterSigners.options}
                value={hook.fields.counterSigners.value}
                onChange={hook.fields.counterSigners.onChange}
                disabled={hook.fields.counterSigners.isDisabled}
                validationErrors={hook.fields.counterSigners.validationErrors}
              />
            }
          />
        )}

        <SidebarField
          label="Currency"
          control={
            <CurrencyEditorControl
              type="sidebar"
              data-testid="quote.currencyControl"
              options={hook.fields.currency.options}
              value={hook.fields.currency.value}
              onChange={hook.fields.currency.onChange}
              disabled={hook.fields.currency.isDisabled}
              validationErrors={hook.fields.currency.validationErrors}
            />
          }
        />

        <SidebarField
          label="Type"
          control={
            <DealTypeEditorControl
              key={hook.fields.quoteDealType.resetKey}
              type="sidebar"
              data-testid="quote.dealTypeControl"
              options={hook.fields.quoteDealType.options}
              value={hook.fields.quoteDealType.value}
              onChange={hook.fields.quoteDealType.onChange}
              disabled={hook.fields.quoteDealType.isDisabled}
              validationErrors={hook.fields.quoteDealType.validationErrors}
            />
          }
        />

        <AmendedScheduleField />

        <SidebarField
          label={hook.fields.validFor.labelField}
          control={
            <ExpirySelectControl
              type="sidebar"
              data-testid="quote.expiryControl"
              duration={hook.fields.validFor.value}
              onChange={hook.fields.validFor.onChange}
              disabled={hook.fields.validFor.isDisabled}
              validationErrors={hook.fields.validFor.validationErrors}
              labelOverride={hook.fields.validFor.labelOverride}
            />
          }
        />

        <SidebarField
          label="Duration"
          control={
            <ContractDurationSelectControl
              type="sidebar"
              data-testid="quote.contractDurationControl"
              options={hook.fields.contractLength.durations}
              duration={hook.fields.contractLength.value}
              onChange={hook.fields.contractLength.onChange}
              disabled={hook.fields.contractLength.isDisabled}
              validationErrors={hook.fields.contractLength.validationErrors}
            />
          }
        />

        {hook.fields.effectiveFrom.visible && (
          <SidebarField
            label="Start date"
            control={
              <EffectiveFromSelectControl
                key={hook.fields.effectiveFrom.resetKey}
                type="sidebar"
                data-testid="quote.effectiveFromControl"
                value={hook.fields.effectiveFrom.value}
                minDate={hook.fields.effectiveFrom.minDate}
                maxDate={hook.fields.effectiveFrom.maxDate}
                onChange={hook.fields.effectiveFrom.onChange}
                disabled={hook.fields.effectiveFrom.isDisabled}
              />
            }
          />
        )}

        {hook.fields.startDate.visible && (
          <SidebarField
            label="Start date"
            control={
              <StartDateSelectControl
                type="sidebar"
                data-testid="quote.startDateControl"
                value={hook.fields.startDate.value}
                onChange={hook.fields.startDate.onChange}
                disabled={hook.fields.startDate.isDisabled}
                validationErrors={hook.fields.startDate.validationErrors}
                optional={hook.fields.startDate.optional}
              />
            }
          />
        )}
        {hook.fields.defaultDueDateDays.visible && (
          <SidebarField
            label="Terms"
            control={
              <PaymentTermsSelectControl
                type="sidebar"
                data-testid="quote.paymentTermsControl"
                value={hook.fields.defaultDueDateDays.value}
                onChange={hook.fields.defaultDueDateDays.onChange}
                disabled={hook.fields.defaultDueDateDays.isDisabled}
                customTerms={hook.fields.defaultDueDateDays.customTerms}
              />
            }
          />
        )}
      </Flex>

      {hook.modals.createRecipient.active && customerId && (
        <CreateRecipientModal onClose={hook.modals.createRecipient.onClose} />
      )}

      {hook.modals.selectScheduleForAmendment.active && (
        <SelectScheduleForAmendment
          onSubmit={hook.modals.selectScheduleForAmendment.onSubmit}
          onCancel={hook.modals.selectScheduleForAmendment.onCancel}
        />
      )}

      {hook.modals.confirmAmendmentChange.changeType && (
        <ConfirmAmendmentChange
          changeType={hook.modals.confirmAmendmentChange.changeType}
          onConfirm={hook.modals.confirmAmendmentChange.onConfirm}
          onCancel={hook.modals.confirmAmendmentChange.onCancel}
        />
      )}
    </Grid>
  )
}
